import { SheetBody, SheetHeader, SheetTitle } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PostFlightMetricChips } from "../../cards/PersonaOpportunityCard/PostFlightMetricChips.js";
import { PostFlightWindowContext } from "../../shared/PostFlightWindowContext.js";
import { postFlightWindowContextFromMetrics } from "../../../lib/post-flight-opportunity-metrics.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";
import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";

import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";

export interface PostFlightDrawerScript {
  id: string;
  title: string;
}

/** Persona drawer layout filled with the winning ad, the lever, and the scripts it produced. */
export function OpportunityHubPostFlightDetails({
  row,
  scripts = [],
  onOpenScript,
}: {
  row: HubChromeOpportunityRow;
  scripts?: PostFlightDrawerScript[];
  onOpenScript?: (scriptId: string) => void;
}) {
  const { t } = useTranslation();
  const metrics = row.postFlight;
  const directionText = metrics?.instruction?.trim() ?? "";
  const showDirection = directionText.length > 0;

  return (
    <>
      <SheetHeader className="border-b px-6 pb-5 pt-6">
        <div className="flex flex-col gap-2 pl-10">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className="break-words text-base leading-snug">{row.angle}</SheetTitle>
            <span className="rounded-sm border border-input bg-background px-1.5 py-0.5 text-[11px] font-medium">
              {personaOpportunityStatusLabel(t, row.status)}
            </span>
          </div>
        </div>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <PersonaOpportunitySupportingMedia row={row} variant="drawer" />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {row.fromLabel ? (
            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.postFlightOpportunity.columns.parent", "Concept")}
              </p>
              <p className="text-sm text-foreground">{row.fromLabel}</p>
            </div>
          ) : null}
          {showDirection ? (
            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.postFlightOpportunity.columns.direction", "Direction")}
              </p>
              <p className="whitespace-pre-wrap text-sm text-foreground">{directionText}</p>
            </div>
          ) : null}
          {metrics ? (
            <PostFlightWindowContext data={postFlightWindowContextFromMetrics(metrics)} />
          ) : null}
          {metrics ? <PostFlightMetricChips metrics={metrics} /> : null}
          {row.why ? (
            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.postFlightOpportunity.why", "Why this ad")}
              </p>
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-muted-foreground">{row.why}</p>
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {t("personaOpportunities.postFlightOpportunity.scripts", "Generated scripts")}
            </p>
            {scripts.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">
                {t("personaOpportunities.postFlightOpportunity.noScripts", "No scripts yet")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {scripts.map((script) => (
                  <li key={script.id}>
                    <button
                      type="button"
                      className="text-left text-sm font-medium text-primary underline-offset-2 hover:underline"
                      onClick={() => onOpenScript?.(script.id)}
                    >
                      {script.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </SheetBody>
    </>
  );
}
